import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft,
  Clock,
  Eye,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Share2,
  Calendar,
  Play,
  ExternalLink,
  Pencil,
  Languages,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import useAiFeatureStatus from '../hooks/useAiFeatureStatus';
import { formatDistanceToNow, format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import PrivateVideoEmbed from '../components/feed/PrivateVideoEmbed';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Blog Post Detail Page
 */
const BlogPost = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userVote, setUserVote] = useState(0); // -1, 0, or 1
  const [voting, setVoting] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [translatedLang, setTranslatedLang] = useState(null);
  const [translating, setTranslating] = useState(false);
  const { flags: aiFlags } = useAiFeatureStatus();

  const TRANSLATE_LANGS = [
    'Spanish', 'French', 'German', 'Italian', 'Portuguese',
    'Japanese', 'Chinese', 'Korean', 'Hindi',
  ];

  const handleTranslate = async (lang) => {
    if (!post?.content) return;
    setTranslating(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(
        `${API_URL}/api/ai/translate`,
        { text: post.content, target_lang: lang },
        { withCredentials: true }
      );
      setTranslatedContent(res.data.translation);
      setTranslatedLang(lang);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.error('Translation is currently disabled.');
      } else {
        toast.error(err?.response?.data?.detail || 'Translation failed');
      }
    } finally {
      setTranslating(false);
    }
  };

  const resetTranslation = () => {
    setTranslatedContent(null);
    setTranslatedLang(null);
  };

  useEffect(() => {
    fetchPost();
    if (user && token) {
      fetchUserVote();
    }
  }, [postId, user, token]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/api/blog/posts/${postId}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else if (response.status === 404) {
        setError('Post not found');
      } else {
        setError('Failed to load post');
      }
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserVote = async () => {
    try {
      const response = await fetch(`${API}/api/blog/posts/${postId}/vote`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserVote(data.user_vote);
      }
    } catch (err) {
      console.error('Error fetching user vote:', err);
    }
  };

  const handleVote = async (vote) => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // If clicking same vote, remove it (toggle)
    const newVote = userVote === vote ? 0 : vote;
    
    setVoting(true);
    try {
      const response = await fetch(`${API}/api/blog/posts/${postId}/vote?vote=${newVote}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserVote(data.user_vote);
        setPost(prev => prev ? { 
          ...prev, 
          upvotes: data.upvotes,
          downvotes: data.downvotes,
          elo_score: data.elo_score
        } : null);
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setVoting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post?.title,
          url: window.location.href
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      // Could show a toast here
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="blog-post-loading">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Skeleton className="h-8 w-24 mb-6" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="h-80 w-full mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="blog-post-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-heading font-medium mb-2">{error || 'Post not found'}</h2>
            <p className="text-muted-foreground mb-4">
              The blog post you&apos;re looking for doesn&apos;t exist or has been removed.
            </p>
            <Button onClick={() => navigate('/blog')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-secondary text-white py-6">
        <div className="container mx-auto px-4 max-w-4xl">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20 mb-4"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Post Header */}
        <article>
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Badge variant="outline">{post.category}</Badge>
              {post.is_video && (
                <Badge variant="secondary">
                  <Play className="w-3 h-3 mr-1" />
                  Video
                </Badge>
              )}
              {post.is_curated && post.external_source && (
                <Badge variant="outline" className="text-muted-foreground">
                  via {post.external_source}
                </Badge>
              )}
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-medium mb-4">
              {post.title}
            </h1>
            
            {post.excerpt && (
              <p className="text-xl text-muted-foreground mb-6">
                {post.excerpt}
              </p>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={post.author_avatar} />
                  <AvatarFallback>{post.author_username?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-medium">{post.author_display_name || post.author_username}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    {post.created_at && format(new Date(post.created_at), 'MMM d, yyyy')}
                    <span>·</span>
                    <Clock className="w-3 h-3" />
                    {post.read_time} min read
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {/* Edit Button - for authors and admins */}
                {user && (user.id === post.author_id || user.is_admin || user.is_sysadmin || user.is_operator_admin) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate(`/blog/admin?edit=${post.id}`)}
                    data-testid="blog-edit-btn"
                  >
                    <Pencil className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                )}
                
                {/* ELO Up/Down Vote Buttons */}
                <div className="flex items-center border rounded-full overflow-hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleVote(1)}
                    disabled={voting}
                    className={`rounded-none px-3 ${userVote === 1 ? 'bg-green-100 text-green-700' : 'hover:bg-green-50'}`}
                    data-testid="blog-upvote-btn"
                  >
                    <ThumbsUp className={`w-4 h-4 ${userVote === 1 ? 'fill-current' : ''}`} />
                  </Button>
                  <span className="px-2 text-sm font-medium min-w-[40px] text-center">
                    {(post.upvotes || 0) - (post.downvotes || 0)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleVote(-1)}
                    disabled={voting}
                    className={`rounded-none px-3 ${userVote === -1 ? 'bg-red-100 text-red-700' : 'hover:bg-red-50'}`}
                    data-testid="blog-downvote-btn"
                  >
                    <ThumbsDown className={`w-4 h-4 ${userVote === -1 ? 'fill-current' : ''}`} />
                  </Button>
                </div>
                <Button variant="ghost" size="sm" onClick={handleShare}>
                  <Share2 className="w-4 h-4" />
                </Button>
                {aiFlags.ai_translation && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={translating}
                        data-testid="blog-translate-btn"
                      >
                        {translating ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Languages className="w-4 h-4" />
                        )}
                        <span className="ml-1 hidden sm:inline">
                          {translatedLang ? translatedLang : 'Translate'}
                        </span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Translate to…</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {TRANSLATE_LANGS.map((lang) => (
                        <DropdownMenuItem
                          key={lang}
                          onClick={() => handleTranslate(lang)}
                          data-testid={`blog-translate-${lang.toLowerCase()}`}
                        >
                          {lang}
                        </DropdownMenuItem>
                      ))}
                      {translatedContent && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={resetTranslation}
                            data-testid="blog-translate-reset"
                          >
                            <RotateCcw className="w-3 h-3 mr-2" />
                            Show original
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </header>

          {/* Featured Image */}
          {post.image_url && (
            <div className="mb-8 rounded-xl overflow-hidden">
              <img
                src={post.image_url}
                alt={post.image_alt || post.title}
                className="w-full h-auto max-h-96 object-cover"
              />
            </div>
          )}

          {/* Video Embed */}
          {post.video_url && (
            <div className="mb-8 rounded-xl overflow-hidden aspect-video">
              <iframe
                src={post.video_url.replace('watch?v=', 'embed/')}
                title={post.title}
                className="w-full h-full"
                allowFullScreen
              />
            </div>
          )}

          {/* Content */}
          <div className="prose prose-lg max-w-none mb-8" data-testid="blog-post-content">
            {translatedContent && (
              <div className="not-prose mb-4 p-3 bg-blue-50 border border-blue-200 rounded flex items-center justify-between gap-2">
                <span className="text-sm text-blue-800 flex items-center gap-2">
                  <Languages className="w-4 h-4" />
                  Translated to <strong>{translatedLang}</strong> by Claude
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetTranslation}
                  data-testid="blog-translate-banner-reset"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Show original
                </Button>
              </div>
            )}
            {/* Splits content on `[youtube:VIDEO_ID]` markers and
                renders the privacy-respecting embed inline so we can
                cite YouTube sources without sending tracking pings on
                page load. */}
            {(() => {
              const raw = translatedContent || post.content || '';
              const parts = raw.split(/\[youtube:([A-Za-z0-9_-]{6,20})\]/);
              return parts.map((seg, i) =>
                i % 2 === 0 ? (
                  <ReactMarkdown key={i}>{seg}</ReactMarkdown>
                ) : (
                  <div key={i} className="my-6 not-prose">
                    <PrivateVideoEmbed
                      url={`https://www.youtube.com/watch?v=${seg}`}
                      title="Companion video"
                    />
                  </div>
                )
              );
            })()}
          </div>

          {/* External Link for Curated Content */}
          {post.is_curated && post.external_url && (
            <Card className="mb-8 bg-muted/50">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">This article is curated from</p>
                  <p className="font-medium">{post.external_source}</p>
                </div>
                <a href={post.external_url} target="_blank" rel="noopener noreferrer">
                  <Button>
                    Read Original
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </CardContent>
            </Card>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {post.tags.map((tag, idx) => (
                <Badge key={`tag-${tag}-${idx}`} variant="secondary" className="text-sm">
                  #{tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Stats */}
          <div className="flex items-center gap-6 text-sm text-muted-foreground border-t pt-6">
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {post.views_count?.toLocaleString()} views
            </span>
            <span className="flex items-center gap-1">
              <ThumbsUp className="w-4 h-4" />
              {post.upvotes || 0} upvotes
            </span>
            <span className="flex items-center gap-1">
              <ThumbsDown className="w-4 h-4" />
              {post.downvotes || 0} downvotes
            </span>
            <span className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4" />
              {post.comments_count} comments
            </span>
          </div>
        </article>

        {/* Back to Blog */}
        <div className="mt-8 pt-8 border-t">
          <Button variant="outline" onClick={() => navigate('/blog')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to All Posts
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
